import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ref, onValue, set, get, push, remove, onChildAdded } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCursor } from '../contexts/CursorContext';
import { useNavigate } from 'react-router-dom';
import './FeetWatercolor.css';

// Primary colors available initially (secondary colors unlocked through mixing)
const PRIMARY_COLORS = [
  { id: 'blue', color: '#4a90e2', name: 'Blue', isPrimary: true },
  { id: 'red', color: '#e74c3c', name: 'Red', isPrimary: true },
  { id: 'yellow', color: '#f1c40f', name: 'Yellow', isPrimary: true },
];

const SECONDARY_COLORS = [
  { id: 'purple', color: '#9b59b6', name: 'Purple', mix: ['blue', 'red'] },
  { id: 'orange', color: '#e67e22', name: 'Orange', mix: ['red', 'yellow'] },
  { id: 'green', color: '#27ae60', name: 'Green', mix: ['blue', 'yellow'] },
];

const FeetWatercolor = () => {
  const { currentUser, userProfile } = useAuth();
  const { setPageOverride } = useCursor();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const containerRef = useRef(null);

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState(PRIMARY_COLORS[0].color);
  const [brushSize, setBrushSize] = useState(30);
  const [brushMode, setBrushMode] = useState('foot'); // 'foot' or 'toe'
  const [unlockedColors, setUnlockedColors] = useState(new Set(['blue', 'red', 'yellow']));
  const [previousColor, setPreviousColor] = useState(null);
  const lastPosRef = useRef({ x: 0, y: 0 });
  const lastStrokeTimeRef = useRef(0);

  // Partner state
  const [partnerCursor, setPartnerCursor] = useState(null);
  const [partnerName, setPartnerName] = useState('Partner');
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const footStepRef = useRef({ count: 0, lastX: 0, lastY: 0 }); // Track steps for alternating feet

  // Override global custom cursor on this page - use default cursor except on canvas
  useEffect(() => {
    // Enable page override to force default cursor
    setPageOverride(true);

    return () => {
      // Disable page override when leaving
      setPageOverride(false);
    };
  }, [setPageOverride]);

  // Generate cursor SVG based on brush mode and color
  const getCursorUrl = useCallback((mode, color) => {
    const encodedColor = encodeURIComponent(color);

    if (mode === 'toe') {
      // Toe cursor - just toes
      return `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="20" viewBox="0 0 40 20"><ellipse cx="8" cy="10" rx="3" ry="4" fill="${encodedColor}" opacity="0.7"/><ellipse cx="16" cy="8" rx="3.5" ry="4.5" fill="${encodedColor}" opacity="0.7"/><ellipse cx="24" cy="9" rx="3" ry="4" fill="${encodedColor}" opacity="0.7"/><ellipse cx="31" cy="11" rx="2.5" ry="3.5" fill="${encodedColor}" opacity="0.7"/><ellipse cx="36" cy="13" rx="2" ry="3" fill="${encodedColor}" opacity="0.7"/></svg>') 20 10, auto`;
    } else {
      // Foot cursor - full foot
      return `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="48" viewBox="0 0 32 48"><ellipse cx="16" cy="24" rx="8" ry="12" fill="${encodedColor}" opacity="0.7"/><ellipse cx="16" cy="34" rx="6" ry="4" fill="${encodedColor}" opacity="0.7"/><ellipse cx="11" cy="14" rx="3" ry="4" fill="${encodedColor}" opacity="0.7"/><ellipse cx="14" cy="11" rx="2.5" ry="3.5" fill="${encodedColor}" opacity="0.7"/><ellipse cx="18" cy="11" rx="2.5" ry="3.5" fill="${encodedColor}" opacity="0.7"/><ellipse cx="21" cy="14" rx="2.5" ry="3.5" fill="${encodedColor}" opacity="0.7"/><ellipse cx="24" cy="17" rx="2" ry="3" fill="${encodedColor}" opacity="0.7"/></svg>') 16 24, auto`;
    }
  }, []);

  // Color mixing function
  const handleColorSelection = useCallback((colorId, colorValue) => {
    // Check if this creates a new color mix
    if (previousColor && previousColor !== colorValue) {
      const prevColorId = [...PRIMARY_COLORS, ...SECONDARY_COLORS].find(c => c.color === previousColor)?.id;

      // Check if this combination creates a secondary color
      SECONDARY_COLORS.forEach(secondary => {
        if (secondary.mix.includes(prevColorId) && secondary.mix.includes(colorId)) {
          setUnlockedColors(prev => new Set([...prev, secondary.id]));
        }
      });
    }

    setPreviousColor(colorValue);
    setCurrentColor(colorValue);
  }, [previousColor]);

  // Canvas dimensions for coordinate normalization
  const canvasDimensionsRef = useRef({ width: 1400, height: 600 });

  // Track processed stroke IDs to avoid duplicates
  const processedStrokesRef = useRef(new Set());

  // Initialize canvas with proper resolution
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      // Store dimensions for coordinate normalization
      canvasDimensionsRef.current = { width: rect.width, height: rect.height };

      // Set display size
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      // Set actual size in memory (scaled for retina)
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctxRef.current = ctx;

      // Fill with paper color
      ctx.fillStyle = '#faf8f0';
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Add subtle paper texture
      addPaperTexture(ctx, rect.width, rect.height);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Add paper texture
  const addPaperTexture = (ctx, width, height) => {
    ctx.save();
    ctx.globalAlpha = 0.015;
    for (let i = 0; i < 500; i++) {
      ctx.fillStyle = `hsl(${30 + Math.random() * 20}, 30%, ${60 + Math.random() * 30}%)`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * width,
        Math.random() * height,
        Math.random() * 2 + 0.5,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
    ctx.restore();
  };

  // Load existing canvas snapshot from Firebase on mount
  useEffect(() => {
    if (!currentUser) return;

    const snapshotRef = ref(database, 'feetWatercolor/snapshot');
    get(snapshotRef).then((snapshot) => {
      if (snapshot.exists() && canvasRef.current) {
        const img = new Image();
        img.onload = () => {
          const ctx = ctxRef.current;
          if (ctx) {
            const dims = canvasDimensionsRef.current;
            ctx.drawImage(img, 0, 0, dims.width, dims.height);
          }
        };
        img.src = snapshot.val();
      }
    });
  }, [currentUser]);

  // Listen to partner's cursor position
  useEffect(() => {
    if (!currentUser) return;

    const usersRef = ref(database, 'users');
    get(usersRef).then((snapshot) => {
      if (snapshot.exists()) {
        const users = snapshot.val();
        const partner = Object.entries(users).find(([uid]) => uid !== currentUser.uid);
        if (partner) {
          const [partnerUid, partnerData] = partner;
          setPartnerName(partnerData.name || 'Partner');

          // Listen to partner's cursor
          const cursorRef = ref(database, `feetWatercolor/cursors/${partnerUid}`);
          onValue(cursorRef, (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val();
              if (Date.now() - data.timestamp < 10000) {
                setPartnerCursor(data);
              } else {
                setPartnerCursor(null);
              }
            }
          });
        }
      }
    });
  }, [currentUser]);

  // Listen to real-time strokes from partner
  useEffect(() => {
    if (!currentUser) return;

    const strokesRef = ref(database, 'feetWatercolor/strokes');

    // Listen for new strokes being added
    const unsubscribe = onChildAdded(strokesRef, (snapshot) => {
      const stroke = snapshot.val();
      const strokeId = snapshot.key;

      // Skip if it's our own stroke or already processed
      if (stroke.userId === currentUser.uid) return;
      if (processedStrokesRef.current.has(strokeId)) return;

      processedStrokesRef.current.add(strokeId);

      // Draw the partner's stroke
      const ctx = ctxRef.current;
      if (ctx && stroke.points && stroke.points.length > 0) {
        const dims = canvasDimensionsRef.current;

        stroke.points.forEach(point => {
          // Convert normalized coordinates back to canvas coordinates
          const x = point.nx * dims.width;
          const y = point.ny * dims.height;
          drawFootprint(ctx, x, y, stroke.color, stroke.size, point.speed, stroke.mode || 'foot', stroke.scrunched || false);
        });
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Save canvas snapshot to Firebase (with compression)
  const saveCanvasSnapshot = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !currentUser) return;

    try {
      // Create a smaller version for faster loading
      const tempCanvas = document.createElement('canvas');
      const maxWidth = 800; // Resize to max 800px wide
      const scale = Math.min(1, maxWidth / canvas.width);

      tempCanvas.width = canvas.width * scale;
      tempCanvas.height = canvas.height * scale;

      const ctx = tempCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);

      // Compress to 60% quality for faster loading
      const dataUrl = tempCanvas.toDataURL('image/png', 0.6);
      const snapshotRef = ref(database, 'feetWatercolor/snapshot');
      await set(snapshotRef, dataUrl);
    } catch (error) {
      console.error('Error saving canvas snapshot:', error);
    }
  }, [currentUser]);

  // Save on page unload/beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Synchronous save attempt using sendBeacon if available
      const canvas = canvasRef.current;
      if (canvas && currentUser) {
        saveCanvasSnapshot();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Save when component unmounts
      saveCanvasSnapshot();
    };
  }, [currentUser, saveCanvasSnapshot]);

  // Periodically clean up old strokes and save snapshot
  useEffect(() => {
    if (!currentUser) return;

    const cleanupInterval = setInterval(async () => {
      // Save current canvas as snapshot
      await saveCanvasSnapshot();

      // Clear old strokes (keep last 30 seconds)
      const strokesRef = ref(database, 'feetWatercolor/strokes');
      const snapshot = await get(strokesRef);
      if (snapshot.exists()) {
        const strokes = snapshot.val();
        const cutoff = Date.now() - 30000;

        Object.entries(strokes).forEach(([key, stroke]) => {
          if (stroke.timestamp < cutoff) {
            remove(ref(database, `feetWatercolor/strokes/${key}`));
          }
        });
      }

      // Clear processed strokes older than cutoff
      processedStrokesRef.current.clear();
    }, 30000); // Every 30 seconds

    return () => clearInterval(cleanupInterval);
  }, [currentUser, saveCanvasSnapshot]);

  // Update cursor position in Firebase (throttled)
  const lastCursorUpdate = useRef(0);
  const updatePartnerCursor = useCallback((x, y) => {
    if (!currentUser) return;

    const now = Date.now();
    if (now - lastCursorUpdate.current < 50) return;
    lastCursorUpdate.current = now;

    const cursorRef = ref(database, `feetWatercolor/cursors/${currentUser.uid}`);
    set(cursorRef, {
      x: x / canvasDimensionsRef.current.width, // Normalized
      y: y / canvasDimensionsRef.current.height,
      color: currentColor,
      userName: userProfile?.name || 'User',
      timestamp: now
    });
  }, [currentUser, currentColor, userProfile]);

  // Get canvas coordinates from event
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Draw a footprint directly on canvas
  const drawFootprint = useCallback((ctx, x, y, color, size, speed = 0, mode = 'foot', scrunched = false) => {
    if (!ctx) return;

    ctx.save();
    ctx.fillStyle = color;

    // Determine if left or right foot based on distance moved
    const dx = x - footStepRef.current.lastX;
    const dy = y - footStepRef.current.lastY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Only alternate when moving significantly
    if (distance > 15) {
      footStepRef.current.count++;
      footStepRef.current.lastX = x;
      footStepRef.current.lastY = y;
    }

    const isLeftFoot = footStepRef.current.count % 2 === 0;
    const footAngle = isLeftFoot ? -0.3 : 0.3; // Angle feet outward

    // Add rotation based on movement direction and foot side
    const moveAngle = Math.atan2(dy, dx);
    const rotationAngle = moveAngle + footAngle;

    // Translate and rotate
    ctx.translate(x, y);
    ctx.rotate(rotationAngle);

    // Now draw at origin (0, 0) since we've translated
    const scale = scrunched ? 0.5 : 1; // More dramatic scrunch
    const s = size * scale;

    if (mode === 'toe') {
      // Toe mode - just draw toes
      ctx.globalAlpha = 0.65;
      const toeSize = s * 0.3;

      const toes = scrunched ? [
        // Scrunched: toes pulled back and clustered
        { ox: -0.2, oy: 0.3, r: 0.4 },
        { ox: 0, oy: 0.1, r: 0.5 },
        { ox: 0.2, oy: 0.3, r: 0.4 },
      ] : [
        // Normal: spread out toes
        { ox: -0.6, oy: 0, r: 0.5 },
        { ox: -0.2, oy: -0.2, r: 0.6 },
        { ox: 0.2, oy: -0.15, r: 0.55 },
        { ox: 0.6, oy: 0.05, r: 0.45 },
        { ox: 0.95, oy: 0.25, r: 0.35 },
      ];

      toes.forEach(toe => {
        ctx.beginPath();
        ctx.ellipse(toe.ox * toeSize, toe.oy * toeSize, toe.r * toeSize, toe.r * toeSize * 1.3, 0, 0, Math.PI * 2);
        ctx.fill();
      });

      // Watercolor bleed for toes
      ctx.globalAlpha = 0.12;
      ctx.beginPath();
      ctx.ellipse(0, 0, toeSize * 1.3, toeSize * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();

    } else {
      // Foot mode - full foot print
      ctx.globalAlpha = 0.6;

      if (speed < 3) {
        // Full foot stamp
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.5, s * 0.7, 0, 0, Math.PI * 2);
        ctx.fill();

        // Heel
        ctx.beginPath();
        ctx.ellipse(0, s * 0.5, s * 0.35, s * 0.25, 0, 0, Math.PI * 2);
        ctx.fill();

        // Toes - dramatically different when scrunched
        const toes = scrunched ? [
          // Scrunched: pulled back toward foot, smaller, closer together
          { ox: -0.2, oy: -0.45, r: 0.12 },
          { ox: 0, oy: -0.5, r: 0.11 },
          { ox: 0.2, oy: -0.45, r: 0.1 },
        ] : [
          // Normal: extended forward, spread out
          { ox: -0.35, oy: -0.7, r: 0.18 },
          { ox: -0.1, oy: -0.85, r: 0.16 },
          { ox: 0.15, oy: -0.8, r: 0.14 },
          { ox: 0.35, oy: -0.7, r: 0.12 },
          { ox: 0.5, oy: -0.55, r: 0.1 },
        ];

        toes.forEach(toe => {
          ctx.beginPath();
          ctx.ellipse(toe.ox * s, toe.oy * s, toe.r * s, toe.r * s * 1.3, 0, 0, Math.PI * 2);
          ctx.fill();
        });

        // Watercolor bleed
        ctx.globalAlpha = 0.15;
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.6, s * 0.85, 0, 0, Math.PI * 2);
        ctx.fill();

      } else if (speed > 15) {
        // Fast - splatter
        const splatterCount = Math.min(Math.floor(speed / 3), 10);
        for (let i = 0; i < splatterCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = Math.random() * speed * 1.2;
          const splatterSize = Math.random() * s * 0.12 + s * 0.04;

          ctx.globalAlpha = 0.25 + Math.random() * 0.25;
          ctx.beginPath();
          ctx.ellipse(
            Math.cos(angle) * dist,
            Math.sin(angle) * dist,
            splatterSize,
            splatterSize * (0.7 + Math.random() * 0.5),
            Math.random() * Math.PI,
            0, Math.PI * 2
          );
          ctx.fill();
        }

        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.25, s * 0.35, 0, 0, Math.PI * 2);
        ctx.fill();

      } else {
        // Medium speed
        ctx.beginPath();
        ctx.ellipse(0, 0, s * 0.35, s * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 0.3;
        for (let i = 0; i < 3; i++) {
          const ox = (i - 1) * s * 0.2;
          ctx.beginPath();
          ctx.ellipse(ox, -s * 0.45, s * 0.08, s * 0.1, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    ctx.restore();
  }, []);

  // Batch points for a stroke segment
  const strokePointsRef = useRef([]);
  const strokeIdRef = useRef(null);

  // Send stroke segment to Firebase
  const sendStrokeSegment = useCallback(() => {
    if (!currentUser || strokePointsRef.current.length === 0) return;

    const strokesRef = ref(database, 'feetWatercolor/strokes');
    const newStrokeRef = push(strokesRef);

    set(newStrokeRef, {
      points: strokePointsRef.current,
      color: currentColor,
      size: brushSize,
      mode: brushMode,
      scrunched: isSpaceHeld,
      userId: currentUser.uid,
      timestamp: Date.now()
    });

    strokePointsRef.current = [];
  }, [currentUser, currentColor, brushSize, brushMode, isSpaceHeld]);

  // Handle drawing
  const handleDraw = useCallback((x, y) => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    const lastPos = lastPosRef.current;
    const dx = x - lastPos.x;
    const dy = y - lastPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const speed = distance;

    // Draw locally
    if (distance > 3) {
      const steps = Math.ceil(distance / 6);
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const ix = lastPos.x + dx * t;
        const iy = lastPos.y + dy * t;
        drawFootprint(ctx, ix, iy, currentColor, brushSize, speed, brushMode, isSpaceHeld);

        // Add to stroke batch (normalized coordinates)
        strokePointsRef.current.push({
          nx: ix / canvasDimensionsRef.current.width,
          ny: iy / canvasDimensionsRef.current.height,
          speed
        });
      }
    } else {
      drawFootprint(ctx, x, y, currentColor, brushSize, speed, brushMode, isSpaceHeld);
      strokePointsRef.current.push({
        nx: x / canvasDimensionsRef.current.width,
        ny: y / canvasDimensionsRef.current.height,
        speed
      });
    }

    lastPosRef.current = { x, y };

    // Send stroke segment every 100ms or 20 points
    const now = Date.now();
    if (now - lastStrokeTimeRef.current > 100 || strokePointsRef.current.length >= 20) {
      sendStrokeSegment();
      lastStrokeTimeRef.current = now;
    }
  }, [currentColor, brushSize, brushMode, isSpaceHeld, drawFootprint, sendStrokeSegment]);

  // Mouse handlers
  const handleMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    setIsDrawing(true);
    lastPosRef.current = coords;
    strokePointsRef.current = [];
    lastStrokeTimeRef.current = Date.now();

    // Draw initial point
    const ctx = ctxRef.current;
    if (ctx) {
      drawFootprint(ctx, coords.x, coords.y, currentColor, brushSize, 0, brushMode, isSpaceHeld);
      strokePointsRef.current.push({
        nx: coords.x / canvasDimensionsRef.current.width,
        ny: coords.y / canvasDimensionsRef.current.height,
        speed: 0
      });
    }
  };

  const handleMouseMove = (e) => {
    const coords = getCanvasCoords(e);
    updatePartnerCursor(coords.x, coords.y);

    if (isDrawing) {
      handleDraw(coords.x, coords.y);
    }
  };

  const handleMouseUp = () => {
    if (isDrawing) {
      setIsDrawing(false);
      sendStrokeSegment(); // Send any remaining points
      saveCanvasSnapshot(); // Save after stroke ends
    }
  };

  const handleMouseLeave = () => {
    if (isDrawing) {
      setIsDrawing(false);
      sendStrokeSegment();
      saveCanvasSnapshot(); // Save after stroke ends
    }
  };

  // Touch handlers
  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const coords = getCanvasCoords(touch);
    setIsDrawing(true);
    lastPosRef.current = coords;
    strokePointsRef.current = [];
    lastStrokeTimeRef.current = Date.now();

    const ctx = ctxRef.current;
    if (ctx) {
      drawFootprint(ctx, coords.x, coords.y, currentColor, brushSize, 0, brushMode, isSpaceHeld);
      strokePointsRef.current.push({
        nx: coords.x / canvasDimensionsRef.current.width,
        ny: coords.y / canvasDimensionsRef.current.height,
        speed: 0
      });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const touch = e.touches[0];
    const coords = getCanvasCoords(touch);
    handleDraw(coords.x, coords.y);
    updatePartnerCursor(coords.x, coords.y);
  };

  const handleTouchEnd = () => {
    setIsDrawing(false);
    sendStrokeSegment();
    saveCanvasSnapshot(); // Save after stroke ends
  };

  // Keyboard handlers for toe scrunch
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        setIsSpaceHeld(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsSpaceHeld(false);

        // Flick paint spray
        if (isDrawing) {
          const ctx = ctxRef.current;
          if (ctx) {
            const { x, y } = lastPosRef.current;
            const sprayPoints = [];

            for (let i = 0; i < 12; i++) {
              const angle = Math.random() * Math.PI * 2;
              const dist = 25 + Math.random() * 60;
              const sx = x + Math.cos(angle) * dist;
              const sy = y + Math.sin(angle) * dist;

              ctx.save();
              ctx.globalAlpha = 0.25 + Math.random() * 0.25;
              ctx.fillStyle = currentColor;
              ctx.beginPath();
              ctx.ellipse(sx, sy, 2 + Math.random() * 5, 2 + Math.random() * 5, 0, 0, Math.PI * 2);
              ctx.fill();
              ctx.restore();

              sprayPoints.push({
                nx: sx / canvasDimensionsRef.current.width,
                ny: sy / canvasDimensionsRef.current.height,
                speed: 25
              });
            }

            // Send spray to Firebase
            if (currentUser && sprayPoints.length > 0) {
              const strokesRef = ref(database, 'feetWatercolor/strokes');
              const newStrokeRef = push(strokesRef);
              set(newStrokeRef, {
                points: sprayPoints,
                color: currentColor,
                size: brushSize * 0.3,
                mode: brushMode,
                userId: currentUser.uid,
                timestamp: Date.now()
              });
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing, currentColor, brushSize, brushMode, currentUser]);

  // Clear canvas
  const handleClearCanvas = async () => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    const dims = canvasDimensionsRef.current;
    ctx.fillStyle = '#faf8f0';
    ctx.fillRect(0, 0, dims.width, dims.height);
    addPaperTexture(ctx, dims.width, dims.height);

    // Clear Firebase data
    if (currentUser) {
      await set(ref(database, 'feetWatercolor/snapshot'), null);
      await set(ref(database, 'feetWatercolor/strokes'), null);
    }

    processedStrokesRef.current.clear();
  };

  // Render partner cursor with denormalized coordinates
  const getPartnerCursorPosition = () => {
    if (!partnerCursor) return null;
    const dims = canvasDimensionsRef.current;
    return {
      x: partnerCursor.x * dims.width,
      y: partnerCursor.y * dims.height
    };
  };

  const partnerPos = getPartnerCursorPosition();

  return (
    <div className="feet-watercolor-page">
      {/* Background orbs */}
      <div className="watercolor-orb feet-orb-1"></div>
      <div className="watercolor-orb feet-orb-2"></div>
      <div className="watercolor-orb feet-orb-3"></div>

      {/* Header */}
      <motion.div
        className="feet-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="feet-title">Footprint Canvas</h1>
        <p className="feet-subtitle">Paint together with your feet!</p>
      </motion.div>

      {/* Return Button */}
      <motion.button
        className="feet-return-button"
        onClick={() => navigate('/home')}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Return Home
      </motion.button>

      {/* Controls */}
      <motion.div
        className="feet-controls"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <div className="brush-controls">
          <label>
            Size:
            <input
              type="range"
              min="15"
              max="60"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
            />
          </label>
          <button
            className={`mode-btn ${brushMode === 'foot' ? 'active' : ''}`}
            onClick={() => setBrushMode('foot')}
          >
            Foot Brush
          </button>
          <button
            className={`mode-btn ${brushMode === 'toe' ? 'active' : ''}`}
            onClick={() => setBrushMode('toe')}
          >
            Toe Brush
          </button>
          <button className="clear-btn" onClick={handleClearCanvas}>
            Clear All
          </button>
        </div>
      </motion.div>

      {/* Canvas Container */}
      <motion.div
        ref={containerRef}
        className="canvas-container"
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
      >
        <canvas
          ref={canvasRef}
          className="painting-canvas"
          style={{ cursor: getCursorUrl(brushMode, currentColor) }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />

        {/* Partner cursor */}
        {partnerPos && (
          <div
            className="partner-cursor-indicator"
            style={{
              left: partnerPos.x,
              top: partnerPos.y,
              backgroundColor: partnerCursor.color
            }}
          >
            <span className="partner-label">{partnerName}</span>
          </div>
        )}
      </motion.div>

      {/* Color Puddles */}
      <motion.div
        className="color-puddles"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <span className="puddles-label">Step into a color:</span>
        <div className="puddles-row">
          {[...PRIMARY_COLORS, ...SECONDARY_COLORS].map((puddle) => {
            const isUnlocked = unlockedColors.has(puddle.id);
            return (
              <button
                key={puddle.id}
                className={`color-puddle ${currentColor === puddle.color ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}`}
                style={{
                  backgroundColor: isUnlocked ? puddle.color : '#d0d0d0',
                  cursor: isUnlocked ? 'pointer' : 'not-allowed',
                  opacity: isUnlocked ? 1 : 0.5
                }}
                onClick={() => isUnlocked && handleColorSelection(puddle.id, puddle.color)}
                title={isUnlocked ? puddle.name : `Mix ${puddle.mix ? puddle.mix.join(' + ') : 'colors'} to unlock`}
                disabled={!isUnlocked}
              />
            );
          })}
        </div>
      </motion.div>

      {/* Instructions */}
      <motion.div
        className="feet-instructions"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <p>
          <strong>Tips:</strong> Move slowly for full footprints • Move fast for splatter •
          Hold <kbd>SPACE</kbd> to scrunch toes, release to flick paint •
          Switch between colors to unlock new mixed colors! • Try Foot or Toe brush modes
        </p>
        {partnerCursor && (
          <p className="partner-online">
            <span className="online-dot"></span>
            {partnerName} is painting too! (You can see each other's foot cursors live!)
          </p>
        )}
      </motion.div>
    </div>
  );
};

export default FeetWatercolor;
